// Canvas renderer: textured rotating Earth (orthographic, precomputed per-pixel lat/lon)
// plus satellites on tilted elliptical orbits with fading trails.
// Ported from the Lovable redesign (spaceman-mission-control/src/components/splash/orbit-canvas.ts).

const INDIA_LON = 78; // degrees east — faces the viewer at start
const TILT = 0.38;    // view tilt (rad)

export function createOrbitScene(canvas, textureSrc, reduced) {
  const ctx = canvas.getContext('2d');
  const globe = document.createElement('canvas');
  const gctx = globe.getContext('2d', { willReadFrequently: true });
  let tex = null;
  let tw = 0, th = 0;
  let W = 0, H = 0, R = 0, cx = 0, cy = 0, G = 0;
  let lut = null; // per-pixel: lon0, v, light
  let out = null;
  let rotation = 0;
  let raf = 0;
  let last = performance.now();
  const sats = [];
  const palette = ['#22e3c4', '#0e9cc0', '#818cf8', '#e8edf5'];

  const img = new Image();
  img.src = textureSrc;
  img.onload = () => {
    const c = document.createElement('canvas');
    tw = c.width = 1024; th = c.height = 512;
    const x = c.getContext('2d');
    x.drawImage(img, 0, 0, tw, th);
    tex = x.getImageData(0, 0, tw, th).data;
  };

  function buildLut() {
    G = Math.max(120, Math.min(360, Math.round(R * 2)));
    globe.width = globe.height = G;
    out = gctx.createImageData(G, G);
    lut = new Float32Array(G * G * 3);
    const r = G / 2;
    const cT = Math.cos(TILT), sT = Math.sin(TILT);
    for (let j = 0; j < G; j++) {
      for (let i = 0; i < G; i++) {
        const k = (j * G + i) * 3;
        const nx = (i + 0.5 - r) / r, ny = (j + 0.5 - r) / r;
        const d = nx * nx + ny * ny;
        if (d > 1) { lut[k + 2] = -1; continue; }
        const nz = Math.sqrt(1 - d);
        // undo view tilt around x-axis
        const y2 = ny * cT - nz * sT;
        const z2 = ny * sT + nz * cT;
        const lat = Math.asin(-y2);
        const lon = Math.atan2(nx, z2);
        lut[k] = lon;
        lut[k + 1] = 0.5 - lat / Math.PI;
        // light from upper-left + rim falloff
        const light = Math.max(0.12, 0.35 + 0.75 * (-0.45 * nx - 0.35 * ny + 0.82 * nz));
        lut[k + 2] = Math.min(1.25, light) * (0.55 + 0.45 * Math.pow(nz, 0.35));
      }
    }
  }

  function resize() {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    W = canvas.clientWidth; H = canvas.clientHeight;
    canvas.width = W * dpr; canvas.height = H * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const wide = W > 900;
    R = Math.min(W, H) * (wide ? 0.27 : 0.3);
    cx = wide ? W * 0.62 : W / 2;
    cy = wide ? H * 0.5 : H * 0.42;
    buildLut();
  }

  function addSat(rx, ry, rot, phase, speed, color) {
    sats.push({ rx, ry, rot, phase, speed, color: color || palette[sats.length % palette.length], trail: [] });
    if (sats.length > 40) sats.shift();
  }

  function seed() {
    addSat(R * 1.35, R * 0.42, -0.35, 0, 0.55);
    addSat(R * 1.6, R * 0.6, 0.5, 2, 0.38);
    addSat(R * 1.9, R * 0.35, -0.9, 4, 0.28);
    addSat(R * 1.22, R * 1.05, 1.2, 1, 0.7);
  }

  function satPos(s, a) {
    const ex = Math.cos(a) * s.rx, ey = Math.sin(a) * s.ry;
    const c = Math.cos(s.rot), sn = Math.sin(s.rot);
    return { x: cx + ex * c - ey * sn, y: cy + ex * sn + ey * c, front: Math.sin(a) > 0 };
  }

  function drawGlobe() {
    if (!lut || !out) return;
    const o = out.data;
    if (tex) {
      const base = (INDIA_LON * Math.PI) / 180 + rotation;
      for (let p = 0, k = 0; p < G * G; p++, k += 3) {
        const q = p * 4;
        const L = lut[k + 2];
        if (L < 0) { o[q + 3] = 0; continue; }
        let u = (lut[k] + base) / (2 * Math.PI) + 0.5;
        u -= Math.floor(u);
        const tx = (u * tw) | 0, ty = Math.min(th - 1, (lut[k + 1] * th) | 0);
        const t = (ty * tw + tx) * 4;
        o[q] = Math.min(255, tex[t] * L);
        o[q + 1] = Math.min(255, tex[t + 1] * L);
        o[q + 2] = Math.min(255, tex[t + 2] * L + 6);
        o[q + 3] = 255;
      }
      gctx.putImageData(out, 0, 0);
      ctx.drawImage(globe, cx - R, cy - R, R * 2, R * 2);
    } else {
      ctx.fillStyle = '#0d1a3a';
      ctx.beginPath(); ctx.arc(cx, cy, R, 0, Math.PI * 2); ctx.fill();
    }
    // atmosphere rim
    const g = ctx.createRadialGradient(cx, cy, R * 0.92, cx, cy, R * 1.18);
    g.addColorStop(0, 'rgba(34,227,196,0)');
    g.addColorStop(0.35, 'rgba(14,156,192,0.45)');
    g.addColorStop(1, 'rgba(14,156,192,0)');
    ctx.fillStyle = g;
    ctx.beginPath(); ctx.arc(cx, cy, R * 1.18, 0, Math.PI * 2); ctx.fill();
  }

  function drawOrbits(back) {
    for (const s of sats) {
      // faint orbit path
      if (back) {
        ctx.save();
        ctx.translate(cx, cy); ctx.rotate(s.rot);
        ctx.strokeStyle = 'rgba(136,146,164,0.10)';
        ctx.lineWidth = 0.6; ctx.setLineDash([2, 5]);
        ctx.beginPath(); ctx.ellipse(0, 0, s.rx, s.ry, 0, 0, Math.PI * 2); ctx.stroke();
        ctx.restore();
      }
      const p = satPos(s, s.phase);
      if (p.front === back) continue;
      if (!reduced) {
        ctx.lineCap = 'round';
        for (let i = 1; i < s.trail.length; i++) {
          const a = s.trail[i - 1], b = s.trail[i];
          ctx.strokeStyle = s.color;
          ctx.globalAlpha = (i / s.trail.length) * 0.7;
          ctx.lineWidth = 1.6 * (i / s.trail.length);
          ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
        }
        ctx.globalAlpha = 1;
      }
      ctx.shadowColor = s.color; ctx.shadowBlur = 10;
      ctx.fillStyle = s.color;
      ctx.fillRect(p.x - 2.5, p.y - 1.2, 5, 2.4);
      ctx.fillRect(p.x - 0.8, p.y - 3, 1.6, 6);
      ctx.shadowBlur = 0;
    }
  }

  function frame(now) {
    const dt = Math.min(0.05, (now - last) / 1000);
    last = now;
    if (!reduced) {
      rotation += dt * 0.12;
      for (const s of sats) {
        s.phase += dt * s.speed;
        const p = satPos(s, s.phase);
        s.trail.push({ x: p.x, y: p.y });
        if (s.trail.length > 28) s.trail.shift();
      }
    }
    ctx.clearRect(0, 0, W, H);
    drawOrbits(true);
    drawGlobe();
    drawOrbits(false);
    raf = requestAnimationFrame(frame);
  }

  resize();
  seed();
  window.addEventListener('resize', resize);
  raf = requestAnimationFrame(frame);

  return {
    launch(x, y) {
      const dx = x - cx, dy = y - cy;
      const dist = Math.max(R * 1.15, Math.hypot(dx, dy));
      addSat(dist, dist * (0.3 + Math.random() * 0.4), Math.atan2(dy, dx), 0, 0.4 + Math.random() * 0.5, '#22e3c4');
    },
    setScale(scale) {
      canvas.style.transform = `scale(${scale})`;
    },
    destroy() {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
    },
  };
}
